//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../../Config';
import { useTeamspaceContext } from '../../../TeamspaceContext';
import {
  TeamspaceTaskResponse,
  Loop,
  PollTeamspaceTask,
  TeamspaceProvisioningStatusResponse,
} from '../../Sidebar/SidebarTypes';
import TenantDataService from '../../../utils/TenantDataService';
import LoopService from '../../../utils/LoopDataService';
import { useUserAuth } from '../../../UserAuth';
import { useLoopContext } from '../../../LoopContext';
import settings from '../../../Config/settings';

type TaskStatus = 'waiting' | 'success' | 'fail';

interface SuccessMsgProps {
  loopName: string;
  description?: string;
  setProgress: (num: number) => void;
  mode: 'loop' | 'teamspace';
  isSuccess?: TaskStatus;
  teamspaceId: string;
  errMsgSuccPage: string;
  setErrMsgSuccPage: (err: string) => void;
  loopId?: string;
  updateTeamspaceTask?: PollTeamspaceTask;
  setTeamspaceCreationFailed?: (failed: boolean) => void;
  setLoopCreationFailed?: (failed: boolean) => void;
  failedTask?: string;
  incompleteTask?: string;
  taskProgressVersion: number;
  taskProgressIndex: number;
  setTaskProgressIndex: (index: number) => void;
  currentDescriptionLoop: string[];
  setCurrentDescriptionLoop: React.Dispatch<React.SetStateAction<string[]>>;
  executeLoopTasks: boolean;
  importDemoData?: boolean;
  demoDataType: string;
  setdemoDataType: (type: string) => void;
}

function SuccessMsg({
  loopName,
  description,
  setProgress,
  mode,
  isSuccess,
  teamspaceId: createdTeamspaceId,
  errMsgSuccPage,
  setErrMsgSuccPage,
  loopId,
  updateTeamspaceTask,
  setTeamspaceCreationFailed,
  failedTask,
  incompleteTask,
  setLoopCreationFailed,
  taskProgressVersion,
  taskProgressIndex,
  setTaskProgressIndex,
  currentDescriptionLoop,
  setCurrentDescriptionLoop,
  executeLoopTasks,
  importDemoData,
  demoDataType,
  setdemoDataType,
}: Readonly<SuccessMsgProps>) {
  const { t } = useTranslation();
  const { services, polling } = useConfig();
  const { user, feature, isAuthenticated } = useUserAuth();
  const [taskStatuses, setTaskStatuses] = useState<TaskStatus[]>([]);
  // integrationTasks removed; frontend now treats categories generically
  const [ariaStatus, setAriaStatus] = useState('');
  const {
    tenantId,
    teamspaceId,
    teamspaceName,
    _setTeamSpaceCreationFailed,
    _setCreateNewTeamSpace,
    _setTeamspacesUpdated,
    _setIncompleteTeamspaceTask,
  } = useTeamspaceContext();
  const [loopTaskSequence, setLoopTaskSequence] = useState<
    { displayName: string; type: string; description: string }[] | undefined
  >(undefined);
  const [creationStepLocked, setCreationStepLocked] = useState(false);
  const { failedLoops, setFailedLoops, setCreateNewLoop } = useLoopContext();

  const userRef = useRef(user);

  // Tracks whether the polling loops should stop early because the component
  // has unmounted. Flipped to true in the cleanup useEffect below so that any
  // in-flight `pollLoopProvisioningStatus`/`pollTeamspaceProvisioningStatus`
  // call can bail out before touching React state.
  const pollingCancelledRef = useRef(false);

  // Create instances of TenantDataService and LoopService using the userRef for non anyn-react code
  const tenantDataService = new TenantDataService(
    () => {
      return userRef.current;
    },
    () => {
      return isAuthenticated;
    },
  );

  const loopService = new LoopService(
    () => {
      return userRef.current;
    },
    () => {
      return isAuthenticated;
    },
  );

  const prevTaskProgressVersion = useRef(taskProgressVersion);

  const updateLoopTaskStatuses = (loop: Loop) => {
    if (!loopTaskSequence?.length) return;

    // Backend may return tasks as an array (withTasks=true) or only a
    // failedTask object. Build a unified list so status mapping works.
    let backendTasks = loop.tasks || [];
    if (backendTasks.length === 0 && loop.failedTask) {
      backendTasks = [loop.failedTask as any];
    }
    const taskStatusByType = new Map(
      backendTasks.map((task) => [task.type?.toUpperCase(), task.status]),
    );

    const nextStatuses = loopTaskSequence.map((task, index) => {
      if (index === 0) {
        return 'success';
      }

      const backendStatus = taskStatusByType.get(task.type?.toUpperCase());

      if (backendStatus === 'COMPLETED') {
        return 'success';
      }

      if (backendStatus === 'FAILED') {
        return 'fail';
      }

      return 'waiting';
    });

    setTaskStatuses(nextStatuses);

    const nextPendingTask = loopTaskSequence.find((task, index) => {
      if (index === 0) return false;
      return nextStatuses[index] === 'waiting';
    });

    if (nextPendingTask) {
      setCurrentDescriptionLoop([
        nextPendingTask.displayName.toUpperCase() +
          ': ' +
          loopName +
          ' ' +
          t('loop'),
        t(nextPendingTask.description),
      ]);
    }

    const totalProvisioningTasks = Math.max(loopTaskSequence.length - 1, 1);
    const completedTasks = nextStatuses
      .slice(1)
      .filter((status) => status === 'success').length;
    const rawProgress = (completedTasks / totalProvisioningTasks) * 100;
    setProgress(loop.status === 'CREATED' ? 100 : Math.min(rawProgress, 99));
  };

  const updateTeamspaceTaskStatuses = (
    provisionStatus: TeamspaceProvisioningStatusResponse,
  ) => {
    const sequence = settings.TEAMSPACE_TASK_SEQUENCE;
    if (!sequence?.length) return;

    const taskStatusByType = new Map(
      (provisionStatus.tasks || []).map((task) => [
        task.type?.toUpperCase(),
        task.status,
      ]),
    );

    const nextStatuses: TaskStatus[] = sequence.map((taskName, index) => {
      // First item ('TEAMSPACE NAME') is the registration step — always success
      // once we reach the polling phase.
      if (index === 0) {
        return 'success';
      }

      // When the overall state is SUCCEEDED, mark all tasks as success even if
      // some products did not produce individual backend tasks.
      if (provisionStatus.state === 'SUCCEEDED') {
        return 'success';
      }

      const backendStatus = taskStatusByType.get(taskName.toUpperCase());

      if (backendStatus === 'COMPLETED') {
        return 'success';
      }

      if (backendStatus === 'FAILED') {
        return 'fail';
      }

      return 'waiting';
    });

    setTaskStatuses(nextStatuses);

    const totalProvisioningTasks = Math.max(sequence.length - 1, 1);
    const completedTasks = nextStatuses
      .slice(1)
      .filter((status) => status === 'success').length;
    const rawProgress = (completedTasks / totalProvisioningTasks) * 100;
    // Only allow 100% when the overall state is SUCCEEDED. Otherwise cap at 99
    // so the UI doesn't advance to the Confirmation page prematurely.
    setProgress(
      provisionStatus.state === 'SUCCEEDED' ? 100 : Math.min(rawProgress, 99),
    );
  };

  const pollTeamspaceProvisioningStatus = async () => {
    if (!createdTeamspaceId) {
      return;
    }

    const isTaskInFlight = (status?: string) => {
      const normalized = (status || '').toUpperCase();
      return (
        normalized === 'WAITING' ||
        normalized === 'PENDING' ||
        normalized === 'REQUESTED' ||
        normalized === 'IN_PROGRESS' ||
        normalized === 'STARTED' ||
        normalized === 'RUNNING'
      );
    };

    let failedSeenAt: number | null = null;
    let attempts = 0;
    while (attempts < (polling?.maxRetries ?? 200)) {
      if (pollingCancelledRef.current) return;
      attempts++;

      const status =
        await tenantDataService.getTeamspaceProvisioningStatus(
          createdTeamspaceId,
        );

      if (pollingCancelledRef.current) return;

      updateTeamspaceTaskStatuses(status);

      if (status.state === 'SUCCEEDED') {
        setTeamspaceCreationFailed?.(false);
        _setTeamSpaceCreationFailed(false);
        setAriaStatus(t('successfully-created'));
        return;
      }

      if (status.state === 'FAILED') {
        if (failedSeenAt === null) {
          failedSeenAt = attempts;
        }

        const tasks = status.tasks || [];
        const hasInFlightTasks = tasks.some((task) =>
          isTaskInFlight(task.status),
        );
        const allTasksCompleted =
          tasks.length > 0
            ? tasks.every((task) => task.status?.toUpperCase() === 'COMPLETED')
            : false;

        if (
          (hasInFlightTasks || allTasksCompleted) &&
          attempts - failedSeenAt < 5
        ) {
          await new Promise((res) =>
            setTimeout(res, polling?.interval ?? 6000),
          );
          continue;
        }

        setTeamspaceCreationFailed?.(true);
        _setTeamSpaceCreationFailed(true);
        throw new Error(t('unexpected-error-try-again-later'));
      }

      await new Promise((res) => setTimeout(res, polling?.interval ?? 6000));
    }

    if (pollingCancelledRef.current) return;
    throw new Error(t('unexpected-error-try-again-later'));
  };

  const pollLoopProvisioningStatus = async () => {
    if (!createdTeamspaceId || !loopId) {
      return;
    }

    const isTaskInFlight = (status?: string) => {
      const normalized = (status || '').toUpperCase();
      return (
        normalized === 'WAITING' ||
        normalized === 'PENDING' ||
        normalized === 'REQUESTED' ||
        normalized === 'IN_PROGRESS' ||
        normalized === 'STARTED' ||
        normalized === 'RUNNING'
      );
    };

    let failedSeenAt: number | null = null;
    let attempts = 0;
    while (attempts < (polling?.maxRetries ?? 200)) {
      if (pollingCancelledRef.current) return;
      attempts++;

      const loop = await loopService.fetchALoop(createdTeamspaceId, loopId, {
        withTasks: 'true',
      });

      if (pollingCancelledRef.current) return;

      updateLoopTaskStatuses(loop);

      if (loop.status === 'CREATED') {
        setLoopCreationFailed?.(false);
        setFailedLoops(
          failedLoops.filter((failedLoop) => failedLoop.id !== loopId),
        );
        setAriaStatus(t('successfully-created'));
        return;
      }

      if (loop.status === 'FAILED') {
        // In async backend execution, one task can fail while another finishes shortly after.
        // Keep polling briefly to let terminal task states settle and reflect final row statuses.
        if (failedSeenAt === null) {
          failedSeenAt = attempts;
        }

        // Backend may expose tasks as an array or a single failedTask object.
        let loopTasks: Array<{ status?: string }> = Array.isArray(loop.tasks)
          ? loop.tasks
          : [];
        if (loopTasks.length === 0 && loop.failedTask) {
          loopTasks = [loop.failedTask];
        }

        const hasInFlightTasks = loopTasks.some((task) =>
          isTaskInFlight(task.status),
        );

        // After a retry, all tasks may already be COMPLETED but the loop-level
        // status hasn't transitioned from FAILED to CREATED yet. Keep polling
        // briefly so the backend can reconcile the status.
        const allTasksCompleted =
          loopTasks.length > 0
            ? loopTasks.every(
                (task) => task.status?.toUpperCase() === 'COMPLETED',
              )
            : false;

        if (
          (hasInFlightTasks || allTasksCompleted) &&
          attempts - failedSeenAt < 5
        ) {
          await new Promise((res) =>
            setTimeout(res, polling?.interval ?? 6000),
          );
          continue;
        }

        const contains = failedLoops.some(
          (failedLoop) => failedLoop.id === loopId,
        );
        if (!contains) {
          setFailedLoops([
            ...failedLoops,
            {
              id: loopId,
              name: loopName,
              mode: 'loop',
              teamSpaceName: teamspaceName,
            },
          ]);
        }
        setLoopCreationFailed?.(true);
        throw new Error(t('unexpected-error-try-again-later'));
      }

      await new Promise((res) => setTimeout(res, polling?.interval ?? 6000));
    }

    if (pollingCancelledRef.current) return;
    throw new Error(t('unexpected-error-try-again-later'));
  };

  /**
   * NEXUS00001892 - This ensures that when the user object changes,
   * the component will re-render and the new value will be updated and read via the reference.
   * This ensures we will use the latest user object values even in "non-react-code".
   */
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Mark polling as cancelled on unmount so any in-flight loop in
  // `pollLoopProvisioningStatus` or `pollTeamspaceProvisioningStatus` exits
  // cleanly instead of calling `setState` on an unmounted component.
  useEffect(() => {
    pollingCancelledRef.current = false;
    return () => {
      pollingCancelledRef.current = true;
    };
  }, []);

  const getCreationIndex = () => {
    if (mode === 'teamspace') return 0;
    if (!loopTaskSequence) return -1;
    return loopTaskSequence.findIndex((task) =>
      typeof task === 'string' ? task === 'CREATION' : task.type === 'CREATION',
    );
  };

  useEffect(() => {
    const handleBeforeUnload = (event: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const fetchLoopTaskStatus = async () => {
      if (!createdTeamspaceId) return;
      try {
        const response = await loopService.loopTaskStatus(
          createdTeamspaceId,
          feature ?? '',
          importDemoData ? demoDataType ?? '' : '',
        );

        // Build a unified ordered task sequence from whatever categories backend returns.
        // Sort categories by their 'order' field, then tasks within each by 'order'.
        const categories = Array.isArray(response?.categories)
          ? [...response.categories]
          : [];

        categories.sort(
          (categoryA, categoryB) =>
            (categoryA.order ?? 0) - (categoryB.order ?? 0),
        );

        const combinedSequence: {
          displayName: string;
          type: string;
          description: string;
        }[] = [];

        categories.forEach((cat) => {
          const tasks = Array.isArray(cat.tasks) ? [...cat.tasks] : [];
          tasks.sort((taskA, taskB) => (taskA.order ?? 0) - (taskB.order ?? 0));
          tasks.forEach((task) => {
            combinedSequence.push({
              displayName: task.displayName,
              type: task.type,
              description: task.description,
            });
          });
        });

        // Guarantee CREATION is first: if present elsewhere move it; if absent add it.
        const creationIndex = combinedSequence.findIndex(
          (task) => task.type?.toUpperCase() === 'CREATION',
        );
        if (creationIndex === -1) {
          combinedSequence.unshift({
            displayName: 'CREATION',
            type: 'CREATION',
            description: 'Creation',
          });
        } else if (creationIndex > 0) {
          const creationTask = combinedSequence.splice(creationIndex, 1)[0];
          combinedSequence.unshift(creationTask);
        }

        setLoopTaskSequence(combinedSequence);
        if (mode === 'loop') {
          setTaskStatuses(Array(combinedSequence.length).fill('waiting'));
        }
      } catch (error) {
        // Silently ignore; errors will be reflected elsewhere via status polling.
      }
    };

    fetchLoopTaskStatus();
  }, [createdTeamspaceId]);

  useEffect(() => {
    if (!incompleteTask) {
      setTaskStatuses(
        Array(
          mode === 'teamspace'
            ? settings.TEAMSPACE_TASK_SEQUENCE.length
            : loopTaskSequence?.length || 0,
        ).fill('waiting'),
      );
    }
  }, [mode]);

  const createTasks = async (taskStep: number) => {
    _setIncompleteTeamspaceTask('');
    try {
      if (mode === 'loop') {
        await pollLoopProvisioningStatus();
        // Signal completion only after polling finishes so that
        // SearchBox / UIShell don't flood the API queue mid-poll.
        setCreateNewLoop(false);
        _setTeamspacesUpdated(true);
        setProgress(100);
        return;
      }

      if (mode === 'teamspace') {
        await pollTeamspaceProvisioningStatus();
        _setCreateNewTeamSpace(false);
        _setTeamspacesUpdated(true);
        setProgress(100);
        return;
      }

      const sequence = loopTaskSequence || [];

      const link = services?.tenant?.url + `/loops/${loopName}`;

      for (taskStep; taskStep < sequence.length; taskStep++) {
        setTaskProgressIndex(taskStep);
        await doOneTask(link, taskStep);
      }
      _setCreateNewTeamSpace(false);
      setCreateNewLoop(false);
      _setTeamspacesUpdated(true);
      setProgress(100);
    } catch (err) {
      _setCreateNewTeamSpace(false);
      setCreateNewLoop(false);
      _setTeamspacesUpdated(true);
      setProgress(0);
      setErrMsgSuccPage(
        err instanceof Error ? err.message : t('unexpected-error'),
      );
    }
  };

  useEffect(() => {
    // Fetch failed task if teamspace creation task previously failed
    const fetchFailedTask = async () => {
      if (failedTask) {
        try {
          const failedTaskResp = await tenantDataService.invokeHREF(failedTask);
          const taskIndex = settings.TEAMSPACE_TASK_SEQUENCE?.indexOf(
            failedTaskResp?.type,
          );
          for (let i = 0; i < taskIndex; i++) {
            updateBoxStatus(i, 'success');
          }
          updateBoxStatus(taskIndex, 'fail');
          setProgress(0);
          setErrMsgSuccPage(t('unexpected-error-try-again-later'));
        } catch (error) {
          console.error('Error fetching teamspace:', error);
        }
      }
    };
    fetchFailedTask();
  }, [failedTask]);

  useEffect(() => {
    if (incompleteTask) {
      resumeTeamspaceCreation();
    }
  }, [incompleteTask]);

  // Resume teamspace task creation when an incomplete teamspace exists
  const resumeTeamspaceCreation = () => {
    if (incompleteTask) {
      const taskIndex =
        settings.TEAMSPACE_TASK_SEQUENCE.indexOf(incompleteTask);
      // Set the status of completed tasks to success and incomplete tasks to waiting
      const taskStatusArray = settings.TEAMSPACE_TASK_SEQUENCE.map((_, idx) =>
        idx < taskIndex ? 'success' : 'waiting',
      );
      setTaskStatuses(taskStatusArray);
      const percent =
        (taskIndex / (settings.TEAMSPACE_TASK_SEQUENCE.length - 1)) * 100;
      setProgress(percent);
      // Continue with task creation at the incomplete task index
      createTasks(taskIndex);
    }
  };

  useEffect(() => {
    const index = executeLoopTasks ? taskProgressIndex : 0;
    if (executeLoopTasks && taskProgressIndex === 1 && loopTaskSequence) {
      // Set initial step to success but waiting for all others
      const taskStatusArray = [
        'success',
        ...Array(loopTaskSequence.length - 1).fill('waiting'),
      ];
      setTaskStatuses(taskStatusArray);
      setAriaStatus(t('success'));
    }
    try {
      if (taskProgressVersion !== prevTaskProgressVersion.current) {
        prevTaskProgressVersion.current = taskProgressVersion;
      }
      if (!incompleteTask) {
        if (isSuccess === 'success') {
          updateBoxStatus(index, 'success');
          setAriaStatus(t('successfully-created'));
          if (
            mode === 'loop' &&
            !executeLoopTasks &&
            loopTaskSequence &&
            loopTaskSequence.length > index + 1
          ) {
            setCurrentDescriptionLoop([
              loopTaskSequence[index + 1].displayName.toUpperCase() +
                ': ' +
                loopName +
                ' ' +
                t('loop'),
              t(loopTaskSequence[index + 1].description),
            ]);
          }
          if (
            (mode === 'loop' && !executeLoopTasks) ||
            (mode === 'teamspace' && taskProgressIndex === 0)
          ) {
            createTasks(1);
          }
        } else if (isSuccess === 'fail') {
          updateBoxStatus(index, 'fail');
          setAriaStatus(t('unexpected-error-try-again-later'));
          setProgress(0);
          setErrMsgSuccPage(t('unexpected-error-try-again-later'));
        } else if (isSuccess === 'waiting') {
          updateBoxStatus(index, 'waiting');
          setAriaStatus(t('waiting'));
        }
      }
    } catch (err) {
      setProgress(0);
      setErrMsgSuccPage(
        err instanceof Error ? err.message : t('unexpected-error'),
      );
    }
  }, [
    executeLoopTasks,
    taskProgressVersion,
    taskProgressIndex,
    isSuccess,
    mode,
  ]);

  useEffect(() => {
    if (updateTeamspaceTask?.selfLink && updateTeamspaceTask?.type) {
      updateTaskStatusOnRetry(
        updateTeamspaceTask.selfLink,
        updateTeamspaceTask.type,
      );
    } else if (updateTeamspaceTask?.noTaskFound) {
      updateTaskStatusOnRetry('', '', updateTeamspaceTask?.noTaskFound);
    }
  }, [updateTeamspaceTask]);

  function updateBoxStatus(i: number, status: TaskStatus) {
    const creationIdx = getCreationIndex();
    setTaskStatuses((prev) => {
      const copy = [...prev];
      // If this is the CREATION step and already locked, do not update
      if (i === creationIdx && creationStepLocked) {
        return copy;
      }
      copy[i] = status;
      // If setting CREATION to success, lock it
      if (i === creationIdx && status === 'success') {
        setCreationStepLocked(true);
      }
      return copy;
    });
  }

  useEffect(() => {
    setCreationStepLocked(false);
    if (loopTaskSequence && mode === 'loop') {
      setTaskStatuses(Array(loopTaskSequence.length).fill('waiting'));
    }
  }, [mode, loopTaskSequence]);

  /**
   * This function is called when a failed teamspace task is retried and the retry API is called.
   * It updates the status of the task box to 'waiting' while polling the task status.
   * Then the final box status is updated.
   * @param taskLink {string} - The self href for the teamspace task
   * @param taskType {string} - The task type that is being retried - i.e. one of the types in TASK_SEQUENCE
   */
  async function updateTaskStatusOnRetry(
    taskLink: string,
    taskType: string,
    noTaskFound: boolean = false,
  ) {
    const taskIndex = taskType
      ? settings.TEAMSPACE_TASK_SEQUENCE?.indexOf(taskType)
      : 1;
    try {
      if (noTaskFound) {
        // Set initial step to success but waiting for all others
        const taskStatusArray = [
          'success',
          ...Array(settings.TEAMSPACE_TASK_SEQUENCE.length - 1).fill('waiting'),
        ];
        setTaskStatuses(taskStatusArray);
        createTasks(taskIndex);
      } else {
        updateBoxStatus(taskIndex, 'waiting');
        const taskStatus = await pollTaskStatus(taskLink);
        if (taskStatus === 'COMPLETED') {
          setTaskProgressIndex(taskIndex);
          updateBoxStatus(taskIndex, 'success');
          const nextStep = taskIndex + 1;
          if (nextStep < settings.TEAMSPACE_TASK_SEQUENCE.length) {
            createTasks(nextStep);
          } else {
            _setTeamspacesUpdated(true);
            setProgress(100);
          }
          setTeamspaceCreationFailed?.(false);
          _setTeamSpaceCreationFailed(false);
          setErrMsgSuccPage('');
          await new Promise((res) => setTimeout(res, 2000));
        } else {
          setTimeout(() => {
            updateBoxStatus(taskIndex, 'fail');
            setErrMsgSuccPage(t('unexpected-error-try-again-later'));
          }, 2000);
          setProgress(0);
          _setTeamspacesUpdated(true);
          setTeamspaceCreationFailed?.(true);
          _setTeamSpaceCreationFailed(true);
        }
      }
    } catch (error) {
      setTimeout(() => {
        updateBoxStatus(taskIndex, 'fail');
        setErrMsgSuccPage(
          error instanceof Error
            ? error.message
            : t('unexpected-error-try-again-later'),
        );
      }, 2000);
      setProgress(0);
      _setTeamspacesUpdated(true);
      setTeamspaceCreationFailed?.(true);
      _setTeamSpaceCreationFailed(true);
    }
  }

  async function doOneTask(teamspaceLink: string, i: number) {
    await executeTask(teamspaceLink, i, settings.TEAMSPACE_TASK_SEQUENCE);
  }

  async function executeTask(link: string, i: number, sequence: string[]) {
    try {
      updateBoxStatus(i, 'waiting');

      const taskBody = {
        teamspace: link,
        type: sequence[i],
        tenant: services?.tenant?.url + '/tenants/' + tenantId,
        name: 'TEAMSPACE',
        async: true,
      };

      const taskResp: TeamspaceTaskResponse =
        await tenantDataService.createTeamspaceTask(taskBody);

      // If response is empty or missing _links, treat as success and skip
      if (!taskResp || Object.keys(taskResp).length === 0 || !taskResp._links) {
        updateBoxStatus(i, 'success');
        setTeamspaceCreationFailed?.(false);
        _setTeamSpaceCreationFailed(false);
        const completedTasks = i;
        const totalTasks = sequence.length - 1;
        const percent = (completedTasks / totalTasks) * 100;
        setProgress(percent);
        await new Promise((res) => setTimeout(res, 2000));
        return;
      }

      const selfLink = taskResp._links.self.href;
      const finalStatus = await pollTaskStatus(selfLink);
      if (finalStatus === 'COMPLETED') {
        setTeamspaceCreationFailed?.(false);
        _setTeamSpaceCreationFailed(false);
        updateBoxStatus(i, 'success');
        await new Promise((res) => setTimeout(res, 2000));
        const completedTasks = i;
        const totalTasks = sequence.length - 1;
        const percent = (completedTasks / totalTasks) * 100;
        setProgress(percent);
      } else {
        setTimeout(() => {
          _setTeamspacesUpdated(true);
          setTeamspaceCreationFailed?.(true);
          _setTeamSpaceCreationFailed(true);
          updateBoxStatus(i, 'fail');
        }, 2000);
        throw new Error(t('unexpected-error-try-again-later'));
      }
    } catch (error) {
      setTimeout(() => {
        _setTeamspacesUpdated(true);
        setTeamspaceCreationFailed?.(true);
        _setTeamSpaceCreationFailed(true);
        updateBoxStatus(i, 'fail');
      }, 2000);
      throw new Error(t('unexpected-error-try-again-later'));
    }
  }

  async function pollTaskStatus(link: string): Promise<string> {
    let attempts = 0;
    while (attempts < (polling?.maxRetries ?? 200)) {
      attempts++;
      const resp = await tenantDataService.invokeHREF(link);
      if (resp.status === 'COMPLETED') return 'COMPLETED';
      if (resp.status === 'FAILED') return 'FAILED';
      await new Promise((res) => setTimeout(res, polling?.interval ?? 6000));
    }
    return 'FAILED';
  }

  const renderProgressRow = (displayName: string, index: number) => {
    const st = taskStatuses[index];
    const isComplete = st === 'success';
    const isFailed = st === 'fail';

    const statusText = isComplete
      ? `${displayName} ${t('completed')}`
      : isFailed
        ? t('unexpected-error')
        : t('progress-status-waiting');

    return (
      <div
        key={index}
        id={`id-success-msg-task-row-${index}`}
        data-testid={`success-msg-task-row-${index}`}
        className="mb-4 grid grid-cols-12 items-center gap-4"
      >
        <div
          id={`id-success-msg-task-name-col-${index}`}
          data-testid={`success-msg-task-name-col-${index}`}
          className="col-span-3"
        >
          <span
            id={`id-success-msg-task-name-${index}`}
            data-testid={`success-msg-task-name-${index}`}
            className="text-sm font-medium text-carbon-dark dark:text-gray-300"
          >
            {displayName}
          </span>
        </div>
        <div
          id={`id-success-msg-task-progress-col-${index}`}
          data-testid={`success-msg-task-progress-col-${index}`}
          className="col-span-9"
        >
          <div
            id={`id-success-msg-task-status-row-${index}`}
            data-testid={`success-msg-task-status-row-${index}`}
            className="mb-1 flex items-center gap-2"
          >
            {isComplete ? (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="#24a148"
                  aria-hidden="true"
                >
                  <path d="M8 16A8 8 0 1 1 8 0a8 8 0 0 1 0 16zm3.293-10.707L6.586 10 4.707 8.121 3.293 9.536 6.586 12.83l6.121-6.121-1.414-1.416z" />
                </svg>
                <span
                  id={`id-success-msg-task-status-text-${index}`}
                  data-testid={`success-msg-task-status-text-${index}`}
                  className="text-xs text-carbon-textSecondary dark:text-brand-disabled"
                >
                  {statusText}
                </span>
              </>
            ) : (
              <span
                id={`id-success-msg-task-status-text-${index}`}
                data-testid={`success-msg-task-status-text-${index}`}
                className={`text-xs ${isFailed ? 'font-medium text-status-failRed' : 'text-carbon-textSecondary dark:text-brand-disabled'}`}
              >
                {statusText}
              </span>
            )}
          </div>
          <div
            id={`id-success-msg-task-track-${index}`}
            data-testid={`success-msg-task-track-${index}`}
            className="h-2 w-full rounded-full bg-carbon-border dark:bg-carbon-textSecondary"
          >
            <div
              id={`id-success-msg-task-fill-${index}`}
              data-testid={`success-msg-task-fill-${index}`}
              className={`h-2 rounded-full transition-all duration-1000 ${
                isComplete
                  ? 'w-full bg-status-successGreen'
                  : isFailed
                    ? 'w-full bg-status-failRed'
                    : 'w-0'
              }`}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      id="id-success-msg-container"
      data-testid="success-msg-container"
      className="m-4 flex max-h-[544px] flex-col overflow-y-auto overflow-x-hidden rounded-[5px]"
    >
      <div
        id="id-success-msg-aria-status"
        data-testid="success-msg-aria-status"
        aria-live="polite"
        className="absolute left-[-9999px] size-px overflow-hidden"
      >
        {ariaStatus}
      </div>
      {errMsgSuccPage && (
        <div
          id="id-success-msg-error"
          data-testid="success-msg-error"
          className="mb-2.5 font-inter text-sm font-normal italic tracking-[0.16px] text-carbon-errorText"
        >
          {errMsgSuccPage}
        </div>
      )}
      {mode === 'teamspace' ? (
        <>
          <h2 className="mb-6 text-xl font-normal text-carbon-dark dark:text-white">
            {loopName} {t('teamspace-progress-heading')}
          </h2>
          <div
            id="id-success-msg-teamspace-tasks"
            data-testid="success-msg-teamspace-tasks"
            className="space-y-4"
          >
            {settings.TEAMSPACE_TASK_SEQUENCE.map((task, idx) => {
              const taskUpper = task.toUpperCase();
              let dn: string;
              if (taskUpper === 'VELOCITY') dn = t('valueStream');
              else if (taskUpper === 'TEAMSPACE NAME')
                dn = t('teamspace-name-display');
              else
                dn = task.charAt(0).toUpperCase() + task.slice(1).toLowerCase();
              return renderProgressRow(dn, idx);
            })}
          </div>
        </>
      ) : (
        <>
          <h2 className="mb-6 text-xl font-normal text-carbon-dark dark:text-white">
            {loopName} {t('loop-progress-heading')}
          </h2>
          <div
            id="id-success-msg-loop-tasks"
            data-testid="success-msg-loop-tasks"
            className="space-y-4"
          >
            {(loopTaskSequence || []).map((task, idx) =>
              renderProgressRow(
                typeof task === 'string' ? task : task.displayName,
                idx,
              ),
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default SuccessMsg;
